import React, {Suspense, useState} from 'react';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {addDate, endDateOfMonth, startDateOfMonth, subDate} from '@shared/dateUtils';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {ROUTE_PATHS} from '@di-shared/routes';
import usePendingHandler from '@shared/usePendingHandler';
import {diApiEu} from '@di-shared/api/di';
import {getYYYYMMddFormat} from '@di-shared/api/di/utils';
import {WebviewUtils} from '@shared/webviewUtils';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import clsx from 'clsx';
import {HistoryDownloadFormProps} from '@di-shared/features/history/download/HistoryDownloadForm/index';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useDashedDatetimeFormatter from '@di-shared/utils/useDashedDatetimeFormatter';
import {emitEventLog} from '@di-shared/features/analytics';
import {useNavigate} from 'react-router';
import useApiError from '@di-shared/features/common/useApiError';

export const PERIOD = {
  SELECT_MONTH: 3,
  CURRENT_MONTH: 0,
  PREVIOUS_1_MONTH: 1,
  PREVIOUS_2_MONTH: 2,
  RECENT_7_DAYS: 7,
  RECENT_30_DAYS: 30,
  RECENT_90_DAYS: 90,
};

enum PeriodType {
  CURRENT_MONTH,
  PREVIOUS_1_MONTH,
  PREVIOUS_2_MONTH,
  RECENT_7_DAYS,
  RECENT_30_DAYS,
  RECENT_90_DAYS,
  CUSTOM_DAYS,
}

export default function ({vin}: HistoryDownloadFormProps) {
  return (
    <Suspense fallback={<FallbackLoading />}>
      <Inner vin={vin} />
    </Suspense>
  );
}

function Inner({vin}: HistoryDownloadFormProps) {
  const {t} = useInferableTranslation();
  const {resolveError} = useApiError();
  const today = new Date();
  const navigate = useNavigate();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const DashedDatetimeFormatter = useDashedDatetimeFormatter();
  const [form, setForm] = useState<{
    period: PeriodType | null;
    startDate: Date | null;
    endDate: Date | null;
  }>(updatePeriod(PeriodType.CURRENT_MONTH));

  function updatePeriod(period: PeriodType) {
    switch (period) {
      case PeriodType.CURRENT_MONTH:
        return {
          period,
          startDate: startDateOfMonth(today),
          endDate: today,
        };
      case PeriodType.PREVIOUS_1_MONTH:
        return {
          period,
          startDate: startDateOfMonth(subDate(today, {months: PERIOD.PREVIOUS_1_MONTH})),
          endDate: endDateOfMonth(subDate(today, {months: PERIOD.PREVIOUS_1_MONTH})),
        };
      case PeriodType.PREVIOUS_2_MONTH:
        return {
          period,
          startDate: startDateOfMonth(subDate(today, {months: PERIOD.PREVIOUS_2_MONTH})),
          endDate: endDateOfMonth(subDate(today, {months: PERIOD.PREVIOUS_2_MONTH})),
        };
      case PeriodType.RECENT_7_DAYS:
        return {
          period,
          startDate: subDate(today, {days: PERIOD.RECENT_7_DAYS - 1}),
          endDate: today,
        };
      case PeriodType.RECENT_30_DAYS:
        return {
          period,
          startDate: subDate(today, {days: PERIOD.RECENT_30_DAYS - 1}),
          endDate: today,
        };
      case PeriodType.CUSTOM_DAYS:
        return {
          period,
          startDate: null,
          endDate: null,
        };
      default:
        return {
          period: null,
          startDate: null,
          endDate: null,
        };
    }
  }

  async function openStartDatePicker() {
    const date = await ccWebviewUtils.openBottomSheetWebViewUrl<Date | null>(
      ROUTE_PATHS.BOTTOM_SHEET_HISTORY_DOWNLOAD_DATEPICKER.getFullPath(),
      {
        selectedDate: form.startDate,
        minDate: form.endDate
          ? subDate(form.endDate, {days: PERIOD.RECENT_90_DAYS - 1})
          : startDateOfMonth(subDate(today, {months: PERIOD.SELECT_MONTH - 1})),
        maxDate: form.endDate ? form.endDate : today,
      }
    );

    if (!date) {
      return;
    }

    setForm((prev) => ({
      period: prev.period,
      startDate: date,
      endDate: prev.endDate,
    }));
  }

  async function openEndDatePicker() {
    const date = await ccWebviewUtils.openBottomSheetWebViewUrl<Date | null>(
      ROUTE_PATHS.BOTTOM_SHEET_HISTORY_DOWNLOAD_DATEPICKER.getFullPath(),
      {
        selectedDate: form.endDate,
        minDate: form.startDate ? form.startDate : startDateOfMonth(subDate(today, {months: PERIOD.SELECT_MONTH - 1})),
        maxDate:
          form.startDate && form.startDate < subDate(today, {days: PERIOD.RECENT_90_DAYS - 1})
            ? addDate(form.startDate, {days: PERIOD.RECENT_90_DAYS - 1})
            : today,
      }
    );

    if (!date) {
      return;
    }

    setForm((prev) => ({
      period: prev.period,
      startDate: prev.startDate,
      endDate: date,
    }));
  }

  const [downloadExcel] = usePendingHandler(
    async () => {
      if (!form.startDate || !form.endDate) {
        return;
      }

      const {data} = await diApiEu.driveHistoryExcel.driveHistoryExcelDownloadToken({
        vin,
        startDate: getYYYYMMddFormat(form.startDate),
        endDate: getYYYYMMddFormat(form.endDate),
      });

      WebviewUtils.downloadUrl({
        fileName:
          `driving_${DashedDatetimeFormatter.format(form.startDate, 'date')}_${DashedDatetimeFormatter.format(form.endDate, 'date')}.xlsx`.replaceAll(
            '-',
            ''
          ),
        url: `${import.meta.env.VITE_DI_EU_API_URL}/drive-history/excel/file?token=${data}`,
        isOpenShare: true,
        mimeType: 'application/vnd.ms-excel',
        messages: {
          complete: t('msg_mytrips_download_toast_completed'),
          failure: t('msg_mytrips_download_toast_try'),
        },
      });

      navigate(ROUTE_PATHS.HISTORY.getFullPath(vin));
    },
    async (error) => {
      await resolveError(error);
      WebviewUtils.showToast(t('msg_mytrips_download_toast_try'));
      navigate(ROUTE_PATHS.HISTORY.getFullPath(vin));
    }
  );

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">
          <span className="blind">{t('ctn_mytrips_download')}</span>
        </h1>
      </Header>
      <main className="content page_setting">
        <section className="section">
          <div className="section_heading">
            <h2 className="section_heading_title">{t('ctn_mytrips_download')}</h2>
            <p className="section_heading_desc">{t('msg_mytrips_download_title')}</p>
          </div>
          <div className="setting_box">
            <h3 className="title_xxsmall">{t('msg_mytrips_download_select_month')}</h3>
            <div className="area_period">
              <ul className="list_period">
                <li className="list_period_item">
                  <button
                    className={clsx('btn btn_filled_tonal', {'is-selected': form.period === PeriodType.PREVIOUS_2_MONTH})}
                    type="button"
                    onClick={() => setForm(updatePeriod(PeriodType.PREVIOUS_2_MONTH))}
                  >
                    {CldrDatetimeFormatter.format(subDate(today, {months: PERIOD.PREVIOUS_2_MONTH}), 'MMM')}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    className={clsx('btn btn_filled_tonal', {'is-selected': form.period === PeriodType.PREVIOUS_1_MONTH})}
                    type="button"
                    onClick={() => setForm(updatePeriod(PeriodType.PREVIOUS_1_MONTH))}
                  >
                    {CldrDatetimeFormatter.format(subDate(today, {months: PERIOD.PREVIOUS_1_MONTH}), 'MMM')}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    className={clsx('btn btn_filled_tonal', {'is-selected': form.period === PeriodType.CURRENT_MONTH})}
                    type="button"
                    onClick={() => setForm(updatePeriod(PeriodType.CURRENT_MONTH))}
                  >
                    {CldrDatetimeFormatter.format(subDate(today, {months: PERIOD.CURRENT_MONTH}), 'MMM')}
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div className="setting_box">
            <h3 className="title_xxsmall">{t('msg_mytrips_download_select_period')}</h3>
            <div className="area_period">
              <ul className="list_period">
                <li className="list_period_item">
                  <button
                    className={clsx('btn btn_filled_tonal', {'is-selected': form.period === PeriodType.RECENT_7_DAYS})}
                    type="button"
                    onClick={() => setForm(updatePeriod(PeriodType.RECENT_7_DAYS))}
                  >
                    {PERIOD.RECENT_7_DAYS}&nbsp;
                    {t('ctn_mytrips_download_day_02')}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    className={clsx('btn btn_filled_tonal', {'is-selected': form.period === PeriodType.RECENT_30_DAYS})}
                    type="button"
                    onClick={() => setForm(updatePeriod(PeriodType.RECENT_30_DAYS))}
                  >
                    {PERIOD.RECENT_30_DAYS}&nbsp;
                    {t('ctn_mytrips_download_day_02')}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    className={clsx('btn btn_filled_tonal', {'is-selected': form.period === PeriodType.CUSTOM_DAYS})}
                    type="button"
                    onClick={() => setForm(updatePeriod(PeriodType.CUSTOM_DAYS))}
                  >
                    {t('ctn_mytrips_download_day_03')}
                  </button>
                </li>
              </ul>
              <div className="select_date_box">
                <div className="btn_box">
                  <button
                    className={clsx('btn btn_full btn_outline btn_date', {
                      type_placeholder: form.period !== PeriodType.CUSTOM_DAYS,
                    })}
                    type="button"
                    disabled={form.period !== PeriodType.CUSTOM_DAYS}
                    onClick={() => openStartDatePicker()}
                  >
                    <span className="body_large_r text_date">
                      {form.startDate ? CldrDatetimeFormatter.format(form.startDate, 'medium') : t('ctn_mytrips_download_startdate')}
                    </span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                  <span
                    className="body_large_b tilde"
                    role="none"
                  >
                    -
                  </span>
                  <button
                    className={clsx('btn btn_full btn_outline btn_date', {
                      type_placeholder: form.period !== PeriodType.CUSTOM_DAYS,
                    })}
                    type="button"
                    disabled={form.period !== PeriodType.CUSTOM_DAYS}
                    onClick={() => openEndDatePicker()}
                  >
                    <span className="body_large_r text_date">
                      {form.endDate ? CldrDatetimeFormatter.format(form.endDate, 'medium') : t('ctn_mytrips_download_enddate')}
                    </span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <div className="floating_action_area">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          disabled={!form.startDate || !form.endDate}
          onClick={() => {
            emitEventLog({
              event: 'driving_records_download',
              form_step: 'download',
              click_text: t('ctn_mytrips_download_ok'),
              click_location: 'body',
            });
            downloadExcel();
          }}
        >
          {t('ctn_mytrips_download_ok')}
        </button>
      </div>
    </div>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();
  const today = new Date();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">
          <span className="blind">{t('ctn_mytrips_download')}</span>
        </h1>
      </Header>
      <main className="content page_setting">
        <section className="section">
          <div className="section_heading">
            <h2
              className="section_heading_title skeleton"
              style={{width: '180px'}}
            >
              {t('ctn_mytrips_download')}
            </h2>
            <p
              className="section_heading_desc skeleton"
              style={{width: '220px'}}
            >
              {t('msg_mytrips_download_title')}
            </p>
          </div>
          <div className="setting_box">
            <h3
              className="title_xxsmall skeleton"
              style={{width: '250px'}}
            >
              {t('msg_mytrips_download_select_month')}
            </h3>
            <div
              className="area_period skeleton"
              style={{width: '374px', height: '38px'}}
            />
          </div>
          <div className="setting_box">
            <h3
              className="title_xxsmall skeleton"
              style={{width: '250px'}}
            >
              {t('msg_mytrips_download_select_period')}
            </h3>
            <div
              className="area_period skeleton"
              style={{width: '374px', height: '84px'}}
            />
          </div>
        </section>
      </main>
      <div className="floating_action_area">
        <button
          type="button"
          className="btn btn_large btn_full skeleton"
        >
          {t('ctn_mytrips_download_ok')}
        </button>
      </div>
    </div>
  );
}
